import { Check, FileSpreadsheet, Upload } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { PERMISSION } from '@obraplan/core/domain/access/permissions';
import type {
  LaborImportOptions,
  LaborImportPreview,
  LaborImportResult,
  PurchaseLinesImportOptions,
  PurchaseLinesImportPreview,
  PurchaseLinesImportResult,
} from '@obraplan/core/application/contracts/purchase-lines';
import type {
  LaborRowData,
  PurchaseLineRowData,
} from '@obraplan/core/domain/schedule/purchase-template/index';
import type { Result } from '@obraplan/core/domain/shared/result';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useContainer, usePermission, useSessionState } from '@/features/session/use-session';
import { cn } from '@/lib/cn';
import { useCommand } from '@/lib/use-command';
import { errorsLeftOut } from './conferencia-model';
import { LaborConferencia } from './LaborConferencia';
import { PurchaseConferencia } from './PurchaseConferencia';
import {
  MAX_PURCHASE_ROWS,
  countLines,
  describeWorkbook,
  joinPreview,
  loadPurchaseWorkbook,
  plural,
  type LoadedPurchaseWorkbook,
} from './purchase-model';

const STEPS = ['Arquivo', 'Itens de compra', 'Mão de obra', 'Resultado'] as const;

/**
 * Os passos por dentro: cada metade — as linhas de compra e a mão de obra — tem a sua
 * conferência e a sua confirmação, e **cada confirmação grava** (cada uma é atômica por si).
 */
type Phase = 'file' | 'lines-check' | 'lines-confirm' | 'labor-check' | 'labor-confirm' | 'result';

const STEP_OF: Readonly<Record<Phase, number>> = {
  file: 0,
  'lines-check': 1,
  'lines-confirm': 1,
  'labor-check': 2,
  'labor-confirm': 2,
  result: 3,
};

const NO_LINE_OPTIONS: PurchaseLinesImportOptions = {
  createMissingFloors: false,
  createMissingActivities: false,
  createMissingDisciplines: false,
  createMissingServices: false,
  createMissingMaterials: false,
};

const NO_LABOR_OPTIONS: LaborImportOptions = { createMissingJobRoles: false };

interface PreviewState<TRow, TPreview> {
  /** As linhas que geraram esta resposta — a junção é por posição, e precisa delas. */
  readonly rows: readonly TRow[];
  readonly preview: TPreview | undefined;
  readonly error: string | undefined;
  readonly loading: boolean;
}

/**
 * A prévia do servidor para um conjunto de linhas: refeita quando as linhas ou as opções
 * mudam, e só enquanto `active`.
 *
 * Mantém a resposta anterior na tela enquanto a nova não chega — trocar uma opção não pode
 * fazer a lista piscar e tirar o foco da caixa que acabou de ser marcada —, mas só a das
 * **mesmas linhas**: outra planilha recomeça do zero.
 */
function useServerPreview<TRow, TPreview>(
  active: boolean,
  rows: readonly TRow[] | undefined,
  options: unknown,
  request: (
    rows: readonly TRow[],
  ) => Promise<Result<TPreview, { readonly message: string }>> | undefined,
): PreviewState<TRow, TPreview> | undefined {
  const [state, setState] = useState<PreviewState<TRow, TPreview> | undefined>(undefined);
  const ask = useRef(request);
  ask.current = request;

  useEffect(() => {
    if (!active || rows === undefined) return;

    if (rows.length === 0) {
      setState({ rows, preview: undefined, error: undefined, loading: false });
      return;
    }
    if (rows.length > MAX_PURCHASE_ROWS) {
      setState({
        rows,
        preview: undefined,
        error: `A planilha tem ${rows.length.toLocaleString('pt-BR')} linhas, e o limite é ${MAX_PURCHASE_ROWS.toLocaleString('pt-BR')} por importação.`,
        loading: false,
      });
      return;
    }

    const pending = ask.current(rows);
    if (pending === undefined) return;

    let alive = true;
    setState((current) =>
      current !== undefined && current.rows === rows
        ? { ...current, error: undefined, loading: true }
        : { rows, preview: undefined, error: undefined, loading: true },
    );
    pending.then(
      (answer) => {
        if (!alive) return;
        setState(
          answer.ok
            ? { rows, preview: answer.value, error: undefined, loading: false }
            : { rows, preview: undefined, error: answer.error.message, loading: false },
        );
      },
      (cause: unknown) => {
        if (!alive) return;
        setState({
          rows,
          preview: undefined,
          error: cause instanceof Error ? cause.message : 'Não foi possível conferir a planilha.',
          loading: false,
        });
      },
    );
    return () => {
      alive = false;
    };
  }, [active, rows, options]);

  return state;
}

export interface PurchaseLinesImportWizardProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

/**
 * O assistente de importação da planilha de itens de compra (Fase 26, ADR-170).
 *
 * Arquivo → conferência das linhas de compra → confirmação → conferência da mão de obra →
 * confirmação → resultado, e o mesmo princípio dos outros assistentes: **nada é gravado até a
 * confirmação**. O arquivo é lido aqui, no navegador (`purchase-model.ts`), e o servidor só
 * recebe linhas normalizadas; a conferência é a resposta dele sobre elas contra o cadastro da
 * obra. Cada metade se confirma separada e é atômica por si: as linhas de compra gravadas
 * ficam mesmo que a mão de obra seja pulada.
 *
 * O envio de requisições continua desligado, e o material de código provisório (`ORC-…`) não
 * vira requisição até ser vinculado ao SKU (RN-347) — a confirmação e o resultado dizem isso.
 */
export function PurchaseLinesImportWizard({ open, onOpenChange }: PurchaseLinesImportWizardProps) {
  const container = useContainer();
  const { session, activeProjectId } = useSessionState();
  const canCreateServices = usePermission(PERMISSION.TAKEOFF_MANAGE);
  const canCreateMaterials = usePermission(PERMISSION.MATERIALS_MANAGE);
  const canCreateJobRoles = usePermission(PERMISSION.EMPLOYEES_MANAGE);
  const fileInput = useRef<HTMLInputElement>(null);

  const [phase, setPhase] = useState<Phase>('file');
  const [workbook, setWorkbook] = useState<LoadedPurchaseWorkbook | undefined>(undefined);
  const [readError, setReadError] = useState<string | undefined>(undefined);
  const [reading, setReading] = useState(false);
  const [lineOptions, setLineOptions] = useState<PurchaseLinesImportOptions>(NO_LINE_OPTIONS);
  const [laborOptions, setLaborOptions] = useState<LaborImportOptions>(NO_LABOR_OPTIONS);
  const [linesResult, setLinesResult] = useState<PurchaseLinesImportResult | undefined>(undefined);
  const [laborResult, setLaborResult] = useState<LaborImportResult | undefined>(undefined);

  const lines = workbook?.lines;
  const labor = workbook?.labor;
  const ready = session !== undefined && activeProjectId !== undefined;

  // ── As prévias do servidor ───────────────────────────────────────────────────────
  const linesPreviewState = useServerPreview<PurchaseLineRowData, PurchaseLinesImportPreview>(
    open && (phase === 'lines-check' || phase === 'lines-confirm'),
    lines?.rows,
    lineOptions,
    (rows) =>
      ready && lines !== undefined
        ? container.previewPurchaseLinesImport.execute(
            {
              projectId: activeProjectId,
              rows,
              options: lineOptions,
              expectedTotals: lines.totals,
            },
            session,
          )
        : undefined,
  );

  const laborPreviewState = useServerPreview<LaborRowData, LaborImportPreview>(
    open && (phase === 'labor-check' || phase === 'labor-confirm'),
    labor?.rows,
    laborOptions,
    (rows) =>
      ready
        ? container.previewLaborImport.execute(
            { projectId: activeProjectId, rows, options: laborOptions },
            session,
          )
        : undefined,
  );

  const linesPreview = linesPreviewState?.preview;
  const linesJoined = useMemo(
    () =>
      linesPreviewState === undefined || linesPreview === undefined
        ? undefined
        : joinPreview(linesPreviewState.rows, linesPreview.rows),
    [linesPreviewState, linesPreview],
  );
  const linesCounts = useMemo(
    () => (linesJoined === undefined ? undefined : countLines(linesJoined)),
    [linesJoined],
  );

  const laborPreview = laborPreviewState?.preview;
  const laborJoined = useMemo(
    () =>
      laborPreviewState === undefined || laborPreview === undefined
        ? undefined
        : joinPreview(laborPreviewState.rows, laborPreview.rows),
    [laborPreviewState, laborPreview],
  );
  const laborCounts = useMemo(
    () => (laborJoined === undefined ? undefined : countLines(laborJoined)),
    [laborJoined],
  );

  // ── Comandos ─────────────────────────────────────────────────────────────────────
  const [confirmLines, confirmLinesState] = useCommand(
    () =>
      container.confirmPurchaseLinesImport.execute(
        {
          projectId: activeProjectId!,
          rows: lines?.rows ?? [],
          options: lineOptions,
          expectedTotals: lines?.totals ?? [],
        },
        session!,
      ),
    {
      silent: true,
      onSuccess: (value) => {
        setLinesResult(value);
        setPhase(labor === undefined ? 'result' : 'labor-check');
      },
    },
  );

  const [confirmLabor, confirmLaborState] = useCommand(
    () =>
      container.confirmLaborImport.execute(
        { projectId: activeProjectId!, rows: labor?.rows ?? [], options: laborOptions },
        session!,
      ),
    {
      silent: true,
      onSuccess: (value) => {
        setLaborResult(value);
        setPhase('result');
      },
    },
  );

  const reset = (): void => {
    setPhase('file');
    setWorkbook(undefined);
    setReadError(undefined);
    setReading(false);
    setLineOptions(NO_LINE_OPTIONS);
    setLaborOptions(NO_LABOR_OPTIONS);
    setLinesResult(undefined);
    setLaborResult(undefined);
  };

  const handleOpenChange = (next: boolean): void => {
    if (!next) reset();
    onOpenChange(next);
  };

  const chooseFile = (chosen: File): void => {
    setReading(true);
    setReadError(undefined);
    void chosen
      .arrayBuffer()
      .then((buffer) => {
        const loaded = loadPurchaseWorkbook(new Uint8Array(buffer), chosen.name);
        if (!loaded.ok) {
          setWorkbook(undefined);
          setReadError(loaded.error);
          return;
        }
        setWorkbook(loaded.value);
        setLineOptions(NO_LINE_OPTIONS);
        setLaborOptions(NO_LABOR_OPTIONS);
      })
      .catch(() => {
        setWorkbook(undefined);
        setReadError('Não consegui ler o arquivo.');
      })
      .finally(() => setReading(false));
  };

  const linesImportable = linesCounts === undefined ? 0 : linesCounts.create + linesCounts.update;
  const canConfirmLines =
    linesCounts !== undefined &&
    linesPreviewState?.loading !== true &&
    linesImportable + linesCounts.unchanged > 0;

  const laborImportable = laborCounts === undefined ? 0 : laborCounts.create + laborCounts.update;
  const canConfirmLabor =
    laborCounts !== undefined &&
    laborPreviewState?.loading !== true &&
    laborImportable + laborCounts.unchanged > 0;

  const step = STEP_OF[phase];

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>Importar itens de compra</DialogTitle>
          <DialogDescription>
            A planilha só altera a obra depois que você confirmar. Até lá, nada é gravado.
          </DialogDescription>
        </DialogHeader>

        <ol className="mb-4 flex flex-wrap gap-2 text-xs">
          {STEPS.map((label, index) => (
            <li
              key={label}
              aria-current={index === step ? 'step' : undefined}
              className={cn(
                'flex items-center gap-1.5 rounded-full border px-3 py-1',
                index === step
                  ? 'border-(--color-brand) font-medium text-(--color-brand)'
                  : 'border-(--color-border-subtle) text-(--color-content-muted)',
              )}
            >
              {index < step ? (
                <Check className="size-3" aria-hidden="true" />
              ) : (
                `${String(index + 1)}. `
              )}
              {label}
            </li>
          ))}
        </ol>

        <div className="flex flex-col gap-4">
          {/* ── 1. Arquivo ─────────────────────────────────────────────────────── */}
          {phase === 'file' && (
            <>
              <div className="flex flex-col items-center gap-3 rounded-md border border-dashed border-(--color-border-subtle) p-8 text-center">
                <FileSpreadsheet
                  className="size-8 text-(--color-content-muted)"
                  aria-hidden="true"
                />
                <p className="max-w-xl text-sm text-(--color-content-muted)">
                  Escolha a planilha de itens de compra (.xlsx). Ela traz o material e o
                  terceirizado de cada serviço por pavimento, com a data de necessidade (aba
                  &ldquo;Itens de compra&rdquo;), e o HH de cada item por função (aba &ldquo;Mão de
                  obra&rdquo;).
                </p>
                <input
                  ref={fileInput}
                  type="file"
                  accept=".xlsx"
                  aria-label="Planilha de itens de compra"
                  className="hidden"
                  onChange={(event) => {
                    const chosen = event.target.files?.[0];
                    if (chosen !== undefined) chooseFile(chosen);
                    event.target.value = '';
                  }}
                />
                <Button
                  type="button"
                  variant={workbook === undefined ? 'primary' : 'secondary'}
                  disabled={reading}
                  onClick={() => fileInput.current?.click()}
                >
                  <Upload aria-hidden="true" />
                  {reading
                    ? 'Lendo…'
                    : workbook === undefined
                      ? 'Escolher planilha'
                      : 'Escolher outra planilha'}
                </Button>
              </div>

              {readError !== undefined && (
                <p role="alert" className="text-sm text-(--color-state-danger)">
                  {readError}
                </p>
              )}

              {workbook !== undefined && (
                <div className="flex flex-col gap-2 rounded-md border border-(--color-border-subtle) p-4">
                  <p className="text-sm">
                    <strong>{workbook.fileName}</strong>
                    <span className="text-(--color-content-muted)">
                      {' '}
                      · {describeWorkbook(workbook)}
                    </span>
                  </p>
                  {workbook.lines === undefined && (
                    <p className="text-xs text-(--color-content-muted)">
                      O arquivo não tem a aba &ldquo;Itens de compra&rdquo;: só a mão de obra será
                      importada.
                    </p>
                  )}
                  {workbook.labor === undefined && (
                    <p className="text-xs text-(--color-content-muted)">
                      O arquivo não tem a aba &ldquo;Mão de obra&rdquo;: só as linhas de compra
                      serão importadas.
                    </p>
                  )}
                </div>
              )}
            </>
          )}

          {/* ── 2. Linhas de compra: conferência ───────────────────────────────── */}
          {phase === 'lines-check' && workbook !== undefined && lines !== undefined && (
            <PurchaseConferencia
              fileName={workbook.fileName}
              lines={lines}
              preview={linesPreview}
              joined={linesJoined}
              isPreviewing={linesPreviewState?.loading === true}
              previewError={linesPreviewState?.error}
              options={lineOptions}
              onOptions={setLineOptions}
              canCreateServices={canCreateServices}
              canCreateMaterials={canCreateMaterials}
            />
          )}

          {/* ── 2. Linhas de compra: confirmação ───────────────────────────────── */}
          {phase === 'lines-confirm' && linesCounts !== undefined && linesPreview !== undefined && (
            <div className="flex flex-col gap-3">
              <h3 className="text-sm font-medium">Pronto para importar as linhas de compra</h3>
              <ul className="flex flex-col gap-1 text-sm">
                <li>
                  <strong>{linesCounts.create.toLocaleString('pt-BR')}</strong>{' '}
                  {linesCounts.create === 1 ? 'linha nova' : 'linhas novas'},{' '}
                  <strong>{linesCounts.update.toLocaleString('pt-BR')}</strong>{' '}
                  {linesCounts.update === 1 ? 'atualizada' : 'atualizadas'} e{' '}
                  <strong>{linesCounts.unchanged.toLocaleString('pt-BR')}</strong> sem mudança.
                </li>
                {lineOptions.createMissingFloors && linesPreview.missing.floors.length > 0 && (
                  <li>
                    Vai criar{' '}
                    {plural(linesPreview.missing.floors.length, 'pavimento', 'pavimentos')}.
                  </li>
                )}
                {lineOptions.createMissingActivities && linesPreview.missing.activities > 0 && (
                  <li>Vai criar {plural(linesPreview.missing.activities, 'frente', 'frentes')}.</li>
                )}
                {lineOptions.createMissingDisciplines &&
                  linesPreview.missing.disciplines.length > 0 && (
                    <li>
                      Vai criar{' '}
                      {plural(linesPreview.missing.disciplines.length, 'disciplina', 'disciplinas')}{' '}
                      no catálogo da obra.
                    </li>
                  )}
                {lineOptions.createMissingServices && linesPreview.missing.services.length > 0 && (
                  <li>
                    Vai criar {plural(linesPreview.missing.services.length, 'serviço', 'serviços')}{' '}
                    no catálogo da obra.
                  </li>
                )}
                {lineOptions.createMissingMaterials &&
                  linesPreview.missing.materials.length > 0 && (
                    <li>
                      Vai criar{' '}
                      {plural(linesPreview.missing.materials.length, 'material', 'materiais')} no
                      catálogo de material.
                    </li>
                  )}
                {linesCounts.error > 0 && (
                  <li className="text-(--color-state-warn)">{errorsLeftOut(linesCounts.error)}</li>
                )}
              </ul>
              <ul className="list-disc rounded-md bg-(--color-surface-muted) p-3 pl-7 text-xs text-(--color-content-muted)">
                <li>
                  A data de cada linha acompanha o item do cronograma por mão de obra da mesma
                  frente e serviço — início menos a margem de chegada da obra — e se recalcula
                  quando o item é importado ou reprogramado (RN-342, RN-343). Linha sem item guarda
                  a data da planilha até o item existir.
                </li>
                <li>
                  Material de código provisório (ORC-…) não vira requisição até ser vinculado ao SKU
                  (RN-347), e o envio de requisições continua desligado.
                </li>
                <li>A quantidade de rateio é provisória e guarda a base do cálculo (RN-345).</li>
              </ul>
              {confirmLinesState.error !== undefined && (
                <p role="alert" className="text-sm text-(--color-state-danger)">
                  {confirmLinesState.error.message}
                </p>
              )}
            </div>
          )}

          {/* ── 3. Mão de obra: conferência ────────────────────────────────────── */}
          {phase === 'labor-check' && workbook !== undefined && labor !== undefined && (
            <>
              {linesResult !== undefined && (
                <p
                  role="status"
                  className="rounded-md border border-(--color-state-ok) bg-(--color-surface-muted) p-3 text-sm"
                >
                  Linhas de compra importadas: {plural(linesResult.created, 'criada', 'criadas')},{' '}
                  {plural(linesResult.updated, 'atualizada', 'atualizadas')} e{' '}
                  {linesResult.unchanged.toLocaleString('pt-BR')} sem mudança.
                </p>
              )}
              <LaborConferencia
                fileName={workbook.fileName}
                labor={labor}
                preview={laborPreview}
                joined={laborJoined}
                isPreviewing={laborPreviewState?.loading === true}
                previewError={laborPreviewState?.error}
                options={laborOptions}
                onOptions={setLaborOptions}
                canCreateJobRoles={canCreateJobRoles}
              />
            </>
          )}

          {/* ── 3. Mão de obra: confirmação ────────────────────────────────────── */}
          {phase === 'labor-confirm' && laborCounts !== undefined && laborPreview !== undefined && (
            <div className="flex flex-col gap-3">
              <h3 className="text-sm font-medium">Pronto para importar a mão de obra</h3>
              <ul className="flex flex-col gap-1 text-sm">
                <li>
                  <strong>{laborCounts.create.toLocaleString('pt-BR')}</strong>{' '}
                  {laborCounts.create === 1 ? 'linha nova' : 'linhas novas'},{' '}
                  <strong>{laborCounts.update.toLocaleString('pt-BR')}</strong>{' '}
                  {laborCounts.update === 1 ? 'atualizada' : 'atualizadas'} e{' '}
                  <strong>{laborCounts.unchanged.toLocaleString('pt-BR')}</strong> sem mudança.
                </li>
                {laborOptions.createMissingJobRoles && laborPreview.missing.jobRoles.length > 0 && (
                  <li>
                    Vai criar {plural(laborPreview.missing.jobRoles.length, 'função', 'funções')} no
                    cadastro de funções.
                  </li>
                )}
                {laborCounts.error > 0 && (
                  <li className="text-(--color-state-warn)">{errorsLeftOut(laborCounts.error)}</li>
                )}
              </ul>
              <p className="rounded-md bg-(--color-surface-muted) p-3 text-xs text-(--color-content-muted)">
                O HH por função não muda o HH do item: ele continua o do cronograma por mão de obra.
                A soma das funções que diverge do HH do item aparece como aviso na conferência
                (RN-351).
              </p>
              {confirmLaborState.error !== undefined && (
                <p role="alert" className="text-sm text-(--color-state-danger)">
                  {confirmLaborState.error.message}
                </p>
              )}
            </div>
          )}

          {/* ── 4. Resultado ───────────────────────────────────────────────────── */}
          {phase === 'result' && (
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-2 rounded-md border border-(--color-state-ok) bg-(--color-surface-muted) p-4">
                <p className="font-medium">Importação concluída.</p>
                {linesResult !== undefined && (
                  <div>
                    <p className="text-sm font-medium">Linhas de compra</p>
                    <ul className="text-sm text-(--color-content-muted)">
                      <li>{plural(linesResult.created, 'linha criada', 'linhas criadas')}</li>
                      <li>
                        {plural(linesResult.updated, 'linha atualizada', 'linhas atualizadas')}
                      </li>
                      <li>{linesResult.unchanged.toLocaleString('pt-BR')} sem mudança</li>
                      {linesResult.createdFloors > 0 && (
                        <li>
                          {plural(
                            linesResult.createdFloors,
                            'pavimento criado',
                            'pavimentos criados',
                          )}
                        </li>
                      )}
                      {linesResult.createdActivities > 0 && (
                        <li>
                          {plural(
                            linesResult.createdActivities,
                            'frente criada',
                            'frentes criadas',
                          )}
                        </li>
                      )}
                      {linesResult.createdDisciplines > 0 && (
                        <li>
                          {plural(
                            linesResult.createdDisciplines,
                            'disciplina criada',
                            'disciplinas criadas',
                          )}
                        </li>
                      )}
                      {linesResult.createdServices > 0 && (
                        <li>
                          {plural(
                            linesResult.createdServices,
                            'serviço criado',
                            'serviços criados',
                          )}
                        </li>
                      )}
                      {linesResult.createdMaterials > 0 && (
                        <li>
                          {plural(
                            linesResult.createdMaterials,
                            'material criado',
                            'materiais criados',
                          )}
                        </li>
                      )}
                      {linesResult.skipped > 0 && <li>{errorsLeftOut(linesResult.skipped)}</li>}
                    </ul>
                  </div>
                )}
                {laborResult !== undefined && (
                  <div>
                    <p className="text-sm font-medium">Mão de obra</p>
                    <ul className="text-sm text-(--color-content-muted)">
                      <li>{plural(laborResult.created, 'linha criada', 'linhas criadas')}</li>
                      <li>
                        {plural(laborResult.updated, 'linha atualizada', 'linhas atualizadas')}
                      </li>
                      <li>{laborResult.unchanged.toLocaleString('pt-BR')} sem mudança</li>
                      {laborResult.createdJobRoles > 0 && (
                        <li>
                          {plural(laborResult.createdJobRoles, 'função criada', 'funções criadas')}
                        </li>
                      )}
                      {laborResult.skipped > 0 && <li>{errorsLeftOut(laborResult.skipped)}</li>}
                    </ul>
                  </div>
                )}
                {labor !== undefined && laborResult === undefined && (
                  <p className="text-sm text-(--color-content-muted)">
                    A mão de obra não foi importada.
                  </p>
                )}
              </div>
              {linesResult !== undefined && (
                <p className="text-xs text-(--color-content-muted)">
                  Material de código provisório (ORC-…) não vira requisição até ser vinculado ao
                  SKU, e o envio de requisições continua desligado.
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          {phase !== 'result' && (
            <Button type="button" variant="secondary" onClick={() => handleOpenChange(false)}>
              {linesResult === undefined ? 'Cancelar' : 'Encerrar'}
            </Button>
          )}

          {phase === 'file' && (
            <Button
              type="button"
              variant="primary"
              disabled={workbook === undefined || reading}
              onClick={() => setPhase(lines !== undefined ? 'lines-check' : 'labor-check')}
            >
              Conferir
            </Button>
          )}

          {phase === 'lines-check' && (
            <>
              <Button type="button" variant="secondary" onClick={() => setPhase('file')}>
                Voltar
              </Button>
              <Button
                type="button"
                variant="primary"
                disabled={!canConfirmLines}
                onClick={() => setPhase('lines-confirm')}
              >
                Revisar e confirmar
              </Button>
            </>
          )}

          {phase === 'lines-confirm' && (
            <>
              <Button
                type="button"
                variant="secondary"
                disabled={confirmLinesState.isPending}
                onClick={() => setPhase('lines-check')}
              >
                Voltar
              </Button>
              <Button
                type="button"
                variant="primary"
                disabled={confirmLinesState.isPending || !canConfirmLines}
                onClick={() => void confirmLines()}
              >
                {confirmLinesState.isPending
                  ? 'Importando…'
                  : linesImportable > 0
                    ? `Importar ${linesImportable.toLocaleString('pt-BR')} ${linesImportable === 1 ? 'linha' : 'linhas'}`
                    : 'Importar'}
              </Button>
            </>
          )}

          {phase === 'labor-check' && (
            <>
              {linesResult === undefined ? (
                <Button type="button" variant="secondary" onClick={() => setPhase('file')}>
                  Voltar
                </Button>
              ) : (
                <Button type="button" variant="secondary" onClick={() => setPhase('result')}>
                  Pular a mão de obra
                </Button>
              )}
              <Button
                type="button"
                variant="primary"
                disabled={!canConfirmLabor}
                onClick={() => setPhase('labor-confirm')}
              >
                Revisar e confirmar
              </Button>
            </>
          )}

          {phase === 'labor-confirm' && (
            <>
              <Button
                type="button"
                variant="secondary"
                disabled={confirmLaborState.isPending}
                onClick={() => setPhase('labor-check')}
              >
                Voltar
              </Button>
              <Button
                type="button"
                variant="primary"
                disabled={confirmLaborState.isPending || !canConfirmLabor}
                onClick={() => void confirmLabor()}
              >
                {confirmLaborState.isPending
                  ? 'Importando…'
                  : laborImportable > 0
                    ? `Importar ${laborImportable.toLocaleString('pt-BR')} ${laborImportable === 1 ? 'linha' : 'linhas'} de HH`
                    : 'Importar'}
              </Button>
            </>
          )}

          {phase === 'result' && (
            <Button type="button" variant="primary" onClick={() => handleOpenChange(false)}>
              Fechar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
